// Typed text of a form that is not sent yet survives leaving the page and coming Back (the browser's Back must
// return to the same view): it is kept in sessionStorage per page or entity, as a small flat object. Only what the
// caller puts in is stored, so the caller never puts a password or any other secret in.

export interface Store { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

const keyOf = (key: string) => `mv.draft.${key}`;
const session = (): Store | undefined => { try { return window.sessionStorage; } catch { return undefined; } };
const sameKind = (a: unknown, b: unknown) => (Array.isArray(a) ? Array.isArray(b) : typeof a === typeof b);

/** The stored draft; keys of the wrong type or unknown to `initial` are ignored, and no draft is `initial`. */
export function readForm<T extends object>(key: string, initial: T, store: Store | undefined = session()): T {
  try {
    const raw = store?.getItem(keyOf(key));
    if (raw) {
      const d = JSON.parse(raw) as Record<string, unknown>;
      if (d && typeof d === 'object' && !Array.isArray(d)) {
        const out: Record<string, unknown> = { ...(initial as Record<string, unknown>) };
        for (const k of Object.keys(initial)) if (k in d && sameKind(d[k], (initial as Record<string, unknown>)[k])) out[k] = d[k];
        return out as T;
      }
    }
  } catch { /* no storage, or damaged: no draft */ }
  return initial;
}

/** Stores the draft; a draft equal to `initial` is no draft and removes the entry. */
export function writeForm<T extends object>(key: string, value: T, initial: T, store: Store | undefined = session()): void {
  try {
    if (JSON.stringify(value) === JSON.stringify(initial)) store?.removeItem(keyOf(key));
    else store?.setItem(keyOf(key), JSON.stringify(value));
  } catch { /* the draft is a convenience */ }
}

/** Whether a draft is stored under the key. */
export function hasForm(key: string, store: Store | undefined = session()): boolean {
  try { return !!store?.getItem(keyOf(key)); } catch { return false; }
}

export function clearForm(key: string, store: Store | undefined = session()): void {
  try { store?.removeItem(keyOf(key)); } catch { /* ignore */ }
}
